import { FilterConfig } from './filter-config';
import { FilterCriteria } from './filter-criteria';
import { FilterData } from './filter-data';

export interface ChangeDetector {
  detectChanges(): void;
}

/**
 * Interface for a filter controller.
 */
export interface FilterController {
  getDropdownWidth(): number;

  /**
   * Called by the dropdown container to get data for the html template.
   */
  getTemplateContext(): any;

  /**
   * Called to determine if the user can apply the local configuration ( enable the 'Apply' button ).
   */
  canApply(): boolean;

  /**
   * Called to determine if the user can clear the local configuration ( enable the 'Clear' button ).
   */
  canClear(): boolean;

  /**
   * Called to initialize the controller with a new criteria object.
   */
  init(filterCriteria: FilterCriteria): void;

  destroy(): void;

  /**
   * Called when the filter dropdown is activated or deactivated.
   */
  onActivate(isActive: boolean): void;

  /**
   * Called when the user hits the 'apply' button in the filter dropdown.  Returns the new filter data, or
   * null if the data is the same as the previous data.
   */
  onApply(): FilterData;

  /**
   * Called when the user hits the escape key to close the dialog without applying any changes.
   */
  onCancel(): void;

  /**
   * Called when the user hits the 'clear' button in the filter dropdown.  This method should reset the filter
   * UI to a clear state, but not update the filter data in the column.
   */
  onClear(): void;
}

/**
 * Common context for creating a filter controller.
 */
export interface FilterControllerContext {
  document: Document;
  changeDetectorRef: ChangeDetector;
}

/**
 * Base filter controller for concrete filters.
 */
export abstract class FilterControllerT<D extends FilterData, P> implements FilterController {
  /**
   * A reference to the current filter criteria object.
   */
  filterCriteria: FilterCriteria;

  /**
   * Declares a local filter data object of the given type.
   */
  localData: D;

  constructor(protected filterContext: FilterControllerContext, dataCreator: () => D) {
    this.dataCreator = dataCreator;
    this.localData = this.dataCreator();
  }

  get changeDetectorRef(): ChangeDetector {
    return this.filterContext.changeDetectorRef;
  }

  get columnId(): string {
    return this.filterCriteria.column?.id;
  }

  get document(): Document {
    return this.filterContext.document;
  }

  /**
   * Shortcut to the source filter config.
   */
  get sourceConfig(): FilterConfig {
    return this.filterCriteria.column?.filterConfig;
  }

  /**
   * Shortcut to the source filter data.
   */
  get sourceData(): D {
    return this.filterCriteria?.filterData as D;
  }

  /**
   * Shortcut to the source filter params.
   */
  get sourceParams(): P {
    return this.sourceConfig.filterParams as P;
  }

  /** FilterController implementation */

  getDropdownWidth(): number {
    return 260;
  }

  getTemplateContext(): any {
    return {
      columnId: this.filterCriteria.column.id,
      data: this.localData,
      params: this.sourceParams
    };
  }

  canApply(): boolean {
    return true;
  }

  canClear(): boolean {
    return this.localData.hasFilter();
  }

  init(filterCriteria: FilterCriteria): void {
    this.filterCriteria = filterCriteria;
    // Ensure params are defined.  This simplifies the html template logic as the params are referenced
    // in the 'getTemplateContext' above.
    this.sourceConfig.filterParams = this.sourceParams || {};
    this.onInit();
  }

  destroy() {
    this.filterContext = undefined;
    this.localData = undefined;
    this.filterCriteria = undefined;
  }

  onActivate(isActive: boolean): void {
    // Sync the model data to the local data.
    this.onInit();
    this.changeDetectorRef.detectChanges();

    if (isActive) {
      this.focusInitialElement();
    }
  }

  onApply(): any {
    // Evaluate the local data.  Execute any processing it needs to do in order to finalize the filter.  This should
    // return 'true' if the filter has changed.
    if (this.evaluateLocalData()) {
      if (this.localData.hasFilter()) {
        // Create a new data object and copy it from the local.
        const newData: D = this.dataCreator();
        newData.copyFrom(this.localData);
        return newData;
      } else {
        // Return 'undefined' to indicate that the filter has been removed.
        return undefined;
      }
    } else {
      // Return 'null' to indicate there is no change in the filter.
      return null;
    }
  }

  onCancel(): void {
    this.onInit();
  }

  onClear(): void {
    this.localData.clear();
  }

  protected ensureInView(containerName: string, elementName: string): void {
    const container = this.getElement(containerName);
    if (!container) {
      return;
    }

    const element = this.getElement(elementName);
    if (!element) {
      return;
    }

    const cTop = container.scrollTop + container.offsetTop;
    const cBottom = cTop + container.clientHeight;

    const eTop = element.offsetTop;
    const eBottom = eTop + element.clientHeight;

    if (eTop < cTop) {
      container.scrollTop -= cTop - eTop;
    } else if (eBottom > cBottom) {
      container.scrollTop += eBottom - cBottom;
    }
  }

  protected focusInitialElement(): void {
    if (this.initialFocusElementName) {
      const element: HTMLInputElement = this.getElement(this.initialFocusElementName) as HTMLInputElement;
      if (element) {
        element.select();
        element.focus();
      }
    }
  }

  protected getElement(name: string): HTMLElement {
    return this.document.getElementById(`${this.filterCriteria.column.id}-${name}`) as HTMLElement;
  }

  /**
   * Called internally to initialize the local data from the source data.
   */
  protected onInit(): void {
    this.localData.copyFrom(this.sourceData);
  }

  /**
   * Called internally during the apply phase to finalize local filter data and return true if the local data differs
   * from the source data.
   */
  protected abstract evaluateLocalData(): boolean;

  /**
   * Constructor parameter passed by derived classes to create an instance of the FilterData object.
   */
  private dataCreator: () => D;

  /**
   * Name of the element which should receive the initial focus.
   */
  protected initialFocusElementName: string;
}
